import { createTransform } from "redux-persist";

/**
 * redux-persist JSON.stringify's every non-blacklisted slice on write
 * (redux/index.tsx blacklists only loginState) and JSON.parse's it back on
 * read - so JSON.stringify(new Date(...)) turns every persisted Date into
 * an ISO-8601 string with millisecond precision and a trailing "Z"
 * (e.g. "2023-04-01T00:00:00.000Z"), and nothing revives it. Every field
 * typed Date in a persisted slice - e.g. dataEntryState.requestAnalysis -
 * is therefore a string at runtime after any page reload, while TypeScript
 * still reports it as Date (AB#35566).
 *
 * This transform is the exact inverse of that JSON.stringify(Date) shape:
 * on rehydrate, any string matching the FULL ISO-8601-with-milliseconds-Z
 * pattern is parsed back into a Date; every other value (including a date
 * string that lacks that exact precision, e.g. "2023-04-01" or
 * "2023-04-01T00:00:00Z") is left untouched (AC3). It walks the whole
 * persisted subtree recursively so newly-added Date fields are covered
 * automatically - no per-path list to keep in sync as DataEntryState (128
 * fields, 47 of them `any`) grows (see Custom.Spec "Sizing").
 */
const ISO_DATE_WITH_MS_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

function reviveIfIsoDate(value: string): string | Date {
    if (!ISO_DATE_WITH_MS_PATTERN.test(value)) {
        return value;
    }
    const parsed = new Date(value);
    // Guards against a corrupted-but-pattern-matching string (e.g. an
    // out-of-range day/month) turning into an Invalid Date object - the test
    // plan requires "never Invalid Date", so an unparseable match is left as
    // the original string instead.
    return isNaN(parsed.getTime()) ? value : parsed;
}

export function reviveDates(value: unknown): unknown {
    if (typeof value === "string") {
        return reviveIfIsoDate(value);
    }
    if (Array.isArray(value)) {
        return value.map(reviveDates);
    }
    if (value !== null && typeof value === "object") {
        const revived: Record<string, unknown> = {};
        for (const key of Object.keys(value as Record<string, unknown>)) {
            revived[key] = reviveDates((value as Record<string, unknown>)[key]);
        }
        return revived;
    }
    // undefined, null, number, boolean: nothing to revive. An absent
    // (undefined) field is dropped by JSON.stringify entirely, so it never
    // reaches here in the first place - it rehydrates as undefined, not
    // Invalid Date, simply by never being visited.
    return value;
}

/**
 * The slices this transform runs on. Deliberately NOT every persisted slice:
 * a createTransform with no whitelist runs on all of them, which meant walking
 * the entire persisted tree - measured at ~2.1 MB on a real transaction, of
 * which landingPageState alone is ~1.8 MB and contains ZERO date-shaped strings.
 * That is ~15 ms of synchronous main-thread work per rehydrate instead of ~2.5 ms,
 * and it exposes slices that have no date problem to a conversion they never
 * needed - the exact over-reach AC3 warns about.
 *
 * Both entries are load-bearing; this was verified against real persisted state
 * rather than reasoned about, so do not trim it to just dataEntryState:
 *  - `dataEntryState` holds requestAnalysis (createdAtUtc / completedAtUtc /
 *    data.serviceDates[].from|to) and documentsList[].lastUpdate.
 *  - `form` holds DATA_ENTRY_FIELDS_FORM.values' date fields - the values the
 *    Quick Entry date pickers actually read. Dropping `form` would silently
 *    regress AB#37843 after a reload: the rehydrated value would stay an ISO
 *    string, toDisplayDate passes non-Dates through untouched, and the picker's
 *    own local-timezone parse would put it back a day.
 *
 * Any NEW persisted slice that carries Date-typed fields must be added here.
 */
const DATE_BEARING_SLICES = ["dataEntryState", "form"];

/**
 * Wired into redux/index.tsx's persistConfig.transforms. Only the outbound
 * (storage -> state, i.e. rehydrate) direction does anything - the inbound
 * (state -> storage) direction is a no-op because JSON.stringify already
 * does the Date -> string conversion redux-persist's storage layer needs;
 * this transform only needs to undo it on the way back.
 */
export const dateRevivalTransform = createTransform(
    (inboundState) => inboundState,
    (outboundState) => reviveDates(outboundState),
    { whitelist: DATE_BEARING_SLICES },
);
